import { Box, Typography } from '@mui/material';
import React from 'react';
import type { Fund } from '../../data/types';
import { useTenantScope } from '../../store/selectors';
import { SectionCard } from '../../ui/primitives';

const pad = (value: string, length: number) => value.slice(0, length).padEnd(length, ' ');
const cents = (value: number, length: number) =>
  String(Math.round(value * 100)).padStart(length, '0');

interface Props {
  fund: Fund;
  description: string;
  paymentDate: string;
  fileName: string;
  payments: { holderId: string; amount: number }[];
}

export default function BankFilePreview({
  fund,
  description,
  paymentDate,
  fileName,
  payments,
}: Props) {
  const scope = useTenantScope();
  const total = payments.reduce((sum, payment) => sum + payment.amount, 0);
  const header = `0                 01WBC       ${pad(fund.bank.accountName.toUpperCase(), 26)}${fund.apcaUserId}${pad(description.toUpperCase(), 12)}${paymentDate.slice(8, 10)}${paymentDate.slice(5, 7)}${paymentDate.slice(2, 4)}`;
  const detail = payments.slice(0, 6).map((payment) => {
    const holder = scope.holderById.get(payment.holderId);
    return `1${holder?.bank.bsb ?? '000-000'}${pad(holder?.bank.accountNumber ?? '', 9)} 50${cents(payment.amount, 10)}${pad((holder?.name ?? '').toUpperCase(), 32)}${pad(`${fund.code} ${description}`, 18)}${fund.bank.bsb}${pad(fund.bank.accountNumber, 9)}`;
  });
  const footer = `7999-999            ${cents(total, 10)}${cents(total, 10)}${cents(0, 10)}                        ${String(payments.length).padStart(6, '0')}`;
  return (
    <SectionCard title="Bank file (ABA)" subtitle={fileName}>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#0F172A',
          color: '#E2E8F0',
          borderRadius: 1.5,
          fontSize: '0.7rem',
          overflowX: 'auto',
          lineHeight: 1.6,
        }}
      >
        {[
          header,
          ...detail,
          payments.length > 6 ? `… ${payments.length - 6} more payment records` : '',
          footer,
        ]
          .filter(Boolean)
          .join('\n')}
      </Box>
      <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 1 }}>
        {payments.length} payments. Holders with unverified bank accounts are held back
        automatically and appear under Payments → Exceptions.
      </Typography>
    </SectionCard>
  );
}
